/* Shared visual atoms used by every report view. */
import { FileText, FileSpreadsheet, Mail } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { useCompactMoney } from "@/lib/useCompactMoney";

export const PALETTE = ["#1B4332", "#1B4332", "#1E40AF", "#60A5FA", "#7C3AED", "#475569", "#94A3B8", "#DC2626"];

export function ExportButtons({ kind, onExport, onShare }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={() => onExport(kind, "pdf")}
        data-testid={`export-${kind}-pdf`}
        className="text-xs sm:text-sm px-2.5 sm:px-3 py-2 bg-black text-white hover:bg-[#1A1A1A] transition-colors flex items-center gap-1.5 border"
        style={{ borderColor: "#000000" }}
      >
        <FileText size={14} /> <span className="hidden sm:inline">Export</span> PDF
      </button>
      <button
        onClick={() => onExport(kind, "xlsx")}
        data-testid={`export-${kind}-xlsx`}
        className="text-xs sm:text-sm px-2.5 sm:px-3 py-2 bg-black text-white hover:bg-[#1A1A1A] transition-colors flex items-center gap-1.5 border"
        style={{ borderColor: "#000000" }}
      >
        <FileSpreadsheet size={14} /> <span className="hidden sm:inline">Export</span> Excel
      </button>
      {onShare && (
        <button
          onClick={() => onShare(kind)}
          data-testid={`share-email-${kind}`}
          title="Share this report by email (honors your mail-routing preference)"
          className="text-xs sm:text-sm px-2.5 sm:px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 transition-colors flex items-center gap-1.5 border"
          style={{ borderColor: "#0b5d9e" }}
        >
          <Mail size={14} /> <span className="hidden sm:inline">Share by</span> Email
        </button>
      )}
    </div>
  );
}

export function Section({ title, rows, totalLabel, totalValue, accent = false }) {
  // Section subtotal is a "headline" number — compact-money toggle applies.
  // Row-level amounts keep full precision because accountants reconcile cents.
  const { money } = useCompactMoney();
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white py-2 px-3 sm:px-4" style={{ background: "#0b5d9e" }}>{title}</div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || r.name || r.category} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
              <td className="px-3 sm:px-4 py-2">{r.name || r.category}</td>
              <td className="px-3 sm:px-4 py-2 text-right font-mono-fin whitespace-nowrap">{fmtMoney(r.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-b border-[var(--ll-ink)]" style={{ background: accent ? "var(--ll-mint)" : "transparent" }}>
            <td className="px-3 sm:px-4 py-2.5 font-medium">{totalLabel}</td>
            <td className="px-3 sm:px-4 py-2.5 text-right font-mono-fin font-medium whitespace-nowrap">{money(totalValue)}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}

export function ReportShell({ title, kind, onExport, onShare, testid, children }) {
  return (
    <div className="ll-card-flat p-4 sm:p-6" data-testid={testid}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Statement of</div>
          <div className="font-display font-bold text-xl sm:text-2xl">{title}</div>
        </div>
        <ExportButtons kind={kind} onExport={onExport} onShare={onShare} />
      </div>
      {children}
    </div>
  );
}
